import { Component, Input, OnChanges } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { toSVG } from 'bwip-js';

/**
 * 바코드 종류. bwip-js의 bcid를 그대로 쓴다. 자주 쓰는 것만 자동완성되게 적어 둔다.
 * 전체 목록: https://github.com/bwipp/postscriptbarcode/wiki/Symbologies-Reference
 */
export type NpBarcodeType = 'qrcode' | 'code128' | 'datamatrix' | 'code39' | (string & {});

/**
 * 바코드·QR을 SVG로 그린다.
 * 생성에 실패해도 예외를 밖으로 내지 않고, 이 영역 안에만 실패를 표시한다.
 */
@Component({
  selector: 'np-barcode',
  standalone: true,
  template: `
    @if (svg) {
      <div class="np-barcode-svg" [innerHTML]="svg"></div>
    } @else if (error) {
      <div class="np-barcode-error" [title]="error">⚠ 바코드 생성 실패</div>
    }
  `,
  styles: [`
    :host { display: block; line-height: 0; }
    .np-barcode-svg { width: 100%; height: 100%; }
    .np-barcode-svg ::ng-deep svg { display: block; width: 100%; height: 100%; }
    .np-barcode-error {
      display: flex; align-items: center; justify-content: center;
      box-sizing: border-box; width: 100%; height: 100%; min-height: 2.5em;
      border: 1px dashed #adb5bd; color: #6c757d; font-size: 12px; line-height: 1.2;
    }
  `],
  host: {
    '[style.width]': 'width ? width + "px" : "100%"',
    '[style.height]': 'height ? height + "px" : "100%"'
  }
})
export class NpBarcodeComponent implements OnChanges {

  /**
   * 바코드에 담을 값. 비어 있으면 아무것도 그리지 않는다.
   * 객체를 주면 JSON 문자열로 바꿔 담는다. 값이 많을수록 QR이 촘촘해진다
   */
  @Input()
  value?: string | number | object | null;

  @Input()
  type: NpBarcodeType = 'code128';

  /** 가로(px). 없으면 부모 너비를 채운다 */
  @Input()
  width?: number;

  /** 세로(px). 없으면 부모 높이를 채우고, 부모 높이가 정해져 있지 않으면 바코드 비율대로 정해진다 (QR은 width만 주면 정사각형) */
  @Input()
  height?: number;

  /** true면 비율을 무시하고 width·height 영역에 꽉 채운다 */
  @Input()
  stretch = false;

  /** bwip-js 옵션 그대로 (예: { eclevel: 'Q', padding: 2 }) */
  @Input()
  options?: Record<string, unknown>;

  svg?: SafeHtml;
  error?: string;

  constructor(private sanitizer: DomSanitizer) {
  }

  ngOnChanges(): void {
    this.render();
  }

  private render(): void {
    this.svg = undefined;
    this.error = undefined;

    let text = '';
    try {
      text = typeof this.value === 'object' && this.value !== null
        ? JSON.stringify(this.value)
        : String(this.value ?? '').trim();
      if (!text) {
        return;
      }

      let svg = toSVG({ ...this.options, bcid: this.type, text });
      if (this.stretch) {
        svg = svg.replace('<svg ', '<svg preserveAspectRatio="none" ');
      }
      // bwip-js가 만든 SVG(경로만 있음)라 그대로 넣는다
      this.svg = this.sanitizer.bypassSecurityTrustHtml(svg);
    } catch (e: any) {
      // 예: "bwipp.ean13badLength#6878: EAN-13 must be 12 or 13 digits"
      const message = String(e?.message ?? e);
      this.error = message.replace(/^bwipp\.[^:]*:\s*/, '');
      console.warn('[np-barcode]', this.type, text, message);
    }
  }

}
